import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckSquare, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1518655048521-f130df041f66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwd29ya3NwYWNlJTIwZGVzayUyMGFic3RyYWN0fGVufDB8fHx8MTc4NDQ3NDI5OXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Assign work. Track deadlines. Stay on top.
          </h2>
          <p className="mt-3 text-slate-200 max-w-md leading-relaxed">
            Create tasks, share a link with anyone, and see progress at a glance with clear color-coded status.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md rounded-md border-slate-200 shadow-sm">
          <CardHeader className="space-y-3">
            <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center">
              <CheckSquare size={18} />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              Log in to TaskFlow
            </CardTitle>
            <p className="text-sm text-slate-500">Manage tasks and track deadlines.</p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="login-default-password-notice">
              <span className="font-semibold">Temporary default password:</span> <code className="font-mono text-[11px] bg-white/60 px-1.5 py-0.5 rounded">Welcome@123</code>
              <div className="mt-1 text-amber-800/80">All accounts have been reset. Log in with this password, then change it from the Users page.</div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full" data-testid="login-submit-btn">
                {busy ? "Logging in…" : "Log in"}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <p className="text-sm text-slate-500 text-center">
                No account?{" "}
                <Link to="/register" className="text-slate-900 font-medium hover:underline" data-testid="login-to-register-link">
                  Create one
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Forgot your password?</DialogTitle>
          </DialogHeader>
          <ForgotForm onDone={() => setForgotOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ForgotForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <div className="space-y-3 text-sm text-slate-700">
        <p><Mail size={13} className="inline mr-1 text-slate-400" /> If an eligible admin account exists for <b>{email}</b>, we've sent a reset link. Check your inbox (and spam).</p>
        <p className="text-xs text-slate-500">The link expires in 1 hour. Only master admins and sub admins can reset via email — regular users should contact a master admin.</p>
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onDone}>Close</Button>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-slate-700">Enter your admin or sub-admin email. We'll send you a link to choose a new password.</p>
      <div className="space-y-1.5">
        <label htmlFor="fe" className="text-sm font-medium text-slate-700">Email</label>
        <Input id="fe" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" autoFocus />
      </div>
      <Button type="submit" disabled={busy} className="w-full" data-testid="forgot-submit-btn">
        {busy ? "Sending…" : "Send reset link"}
      </Button>
      <p className="text-[11px] text-slate-500">Regular users don't receive email reset — contact a master admin to have your password reset from the Users page.</p>
    </form>
  );
}

